import type {
  Challenge,
  SignInBody,
  User,
  SignIn,
  ParticipantScore,
  Participant,
  UserUpdate,
  UserHighestScore,
  Address,
  CampaignDataTranslations,
  LeaderboardParams,
  LeaderboardResponse,
  CampaignTranslations,
  CampaignGame,
  Campaign,
  CampaignUnit,
  Prize,
  TicketCodeResponse,
  TicketCode,
  Product,
  Order,
  ProductOrder,
  CampaignQuiz,
  QuizAnswer,
  QuizAnswerResponse,
  ResponseErrorInfo
} from './types';

import {
  type Langs,
  setUserData,
  setUserPrize,
  type UserState
} from '@/redux/user';

import {
  getCampaignId,
  setCampaignData,
  getCampaignGame,
  getCampaignGames,
  setCampaignTranslations
} from '@/redux/campaign';

import Store from '@/redux';

import Fetch from '@/services/api/Fetch';
import { setAuthData } from '@/redux/auth';
import { getAuthCode } from '@/services/app/mcd';

import { type QuizzesState, setQuizzesData } from '@/redux/quizzes';
import { setChallengesData, setChallengeData } from '@/redux/challenges';

class APIService extends Fetch {
  private readonly gameSlugs: string[] = [];

  /**
   * Auth Endpoints:
   */
  public revokeToken(): void {
    Store.dispatch(
      setAuthData({
        refreshExpiresAt: null,
        accessExpiresAt: null,
        refreshToken: null,
        accessToken: null
      })
    );
  }

  public async login(user: UserState): Promise<void> {
    const campaignId = getCampaignId(Store.getState());

    if (campaignId && user.email && !this.startedRequests('auth/signin')) {
      const { challenges, ...me } = await this.signIn({
        casAuthToken: await this.getAuthToken(),
        firstName: user.firstname,
        lastName: user.lastname,
        deviceId: user.deviceId,
        email: user.email,
        campaignId
      });

      challenges &&
        Store.dispatch(setChallengesData(challenges as Challenge[]));

      Store.dispatch(setUserData({ ...user, ...me }));
    }
  }

  private async signIn(data: SignInBody): Promise<User> {
    const { token, me } = await this.post<SignIn>('auth/signin', data);

    const { accessToken, refreshToken, accessExpiresIn, refreshExpiresIn } =
      token;

    const refreshExpiresAt = this.getAccessExpiration(refreshExpiresIn);
    const accessExpiresAt = this.getAccessExpiration(accessExpiresIn);

    Store.dispatch(
      setAuthData({
        refreshExpiresAt,
        accessExpiresAt,
        refreshToken,
        accessToken
      })
    );

    return me;
  }

  private async getAuthToken(): Promise<string | undefined> {
    const casClientId = import.meta.env.VITE_CAS_CLIENT_ID as string;
    return casClientId && (await getAuthCode(casClientId));
  }

  /**
   * User Endpoints:
   */
  public async submitScore(
    id: string,
    score: number | ParticipantScore
  ): Promise<Participant> {
    const data = (typeof score === 'number' && { score }) || score;

    return this.patch<Participant>(
      `participants/${id}/score`,
      data as ParticipantScore
    );
  }

  public async getUser(): Promise<User> {
    const { challenges, ...user } = await this.get<User>('users/me');
    challenges && Store.dispatch(setChallengesData(challenges as Challenge[]));
    Store.dispatch(setUserData(user as UserState));
    return user;
  }

  public async updateUser(data: UserUpdate): Promise<User> {
    const { challenges, ...user } = await this.patch<User>('users/me', data);
    challenges && Store.dispatch(setChallengesData(challenges as Challenge[]));
    Store.dispatch(setUserData(user as UserState));
    return user;
  }

  public async getHighestScore(gameIndex?: number): Promise<number | null> {
    const gameId = this.getCampaignGameId(gameIndex);

    return (
      await this.get<UserHighestScore>(
        `campaign-games/${gameId}/my-highest-score`
      )
    ).score;
  }

  public async createParticipant(gameIndex?: number): Promise<Participant> {
    if (!this.startedRequests('participants')) {
      return this.post<Participant>('participants', {
        campaignGameId: this.getCampaignGameId(gameIndex)
      });
    }

    throw new Error('Already creating a participant.');
  }

  public async updateAddress(id: string, data: Address): Promise<Participant> {
    return this.patch<Participant>(`participants/${id}/address`, data);
  }

  /**
   * Campaign Endpoints:
   */
  public async setCampaignData(
    language: Langs
  ): Promise<string | void> {
    language ??= import.meta.env.VITE_USER_LANGUAGE as Langs;
    const campaignId = getCampaignId(Store.getState());
    const slug = this.getCampaignSlug();
    if (campaignId) return campaignId;

    if (!this.startedRequests(`campaigns/${slug}`, 'translations')) {
      const { translations: translation = {}, ...campaign } =
        await this.getCampaignBySlug(slug);

      Store.dispatch(setCampaignData({ ...campaign, translations: {} }));

      !this.gameSlugs.length &&
        this.gameSlugs.concat(campaign.campaignGames.map(({ slug }) => slug));

      const translations = await this.setCampaignTranslations(
        translation,
        campaign.id,
        language
      );

      Store.dispatch(setCampaignTranslations(translations));
      return campaign.id;
    }
  }

  private setCampaignDataTranslations(
    translations: Record<string, string>,
    translation: CampaignDataTranslations,
    language: Langs,
    separator = '',
    parent = ''
  ): void {
    for (const key in translation) {
      const translationKey = (parent && `${parent}${separator}`) + key;
      const translationCopy = translation[key];

      typeof translationCopy === 'string'
        ? (translations[translationKey] = translationCopy)
        : language in translationCopy
          ? (translations[translationKey] = translationCopy[language] as string)
          : this.setCampaignDataTranslations(
              translations,
              translationCopy,
              language,
              separator,
              key
            );
    }
  }

  public async getCampaignLeaderboard(
    leaderboardParams: LeaderboardParams
  ): Promise<LeaderboardResponse> {
    const { gameIndex, ...params } = leaderboardParams;
    const gameId = this.getCampaignGameId(gameIndex);

    return this.get<LeaderboardResponse>(
      `campaign-games/${gameId}/leaderboard`,
      params
    );
  }

  private async getCampaignTranslations(
    campaignId: string,
    locale: string
  ): Promise<CampaignTranslations | void> {
    if (this.startedRequests('translations')) return;

    return this.get<CampaignTranslations>('translations', {
      campaignId,
      locale
    });
  }

  private async setCampaignTranslations(
    translation: CampaignDataTranslations,
    campaignId: string,
    language: Langs,
    separator = '_'
  ): Promise<Record<string, string>> {
    const translations: Record<string, string> = {};

    this.setCampaignDataTranslations(
      translations,
      translation,
      language,
      separator
    );

    const campaignTranslations = await this.getCampaignTranslations(
      campaignId,
      language
    );

    Array.isArray(campaignTranslations) &&
      campaignTranslations.reduce((translations, { key, data }) => {
        for (const d in data) translations[`${key}${separator}${d}`] = data[d];

        return translations;
      }, translations);

    return translations;
  }

  private getCampaignSlug(): string {
    return `${import.meta.env.VITE_CAMPAIGN_BASE_SLUG}`;
  }

  private getCampaignGameId(gameIndex?: number): string {
    return gameIndex !== undefined
      ? this.getCampaignGame(gameIndex).id
      : getCampaignGame(Store.getState()).id;
  }

  public getCampaignGameSlug(gameIndex?: number): string {
    return gameIndex !== undefined
      ? this.gameSlugs[gameIndex]
      : getCampaignGame(Store.getState()).slug;
  }

  public getCampaignGame(gameIndex?: number): CampaignGame {
    if (gameIndex === undefined) return getCampaignGame(Store.getState());
    const game = this.getCampaignGameSlug(gameIndex);

    return getCampaignGames(Store.getState()).find(
      ({ slug }) => slug === game
    ) as CampaignGame;
  }

  private async getCampaignBySlug(slug: string): Promise<Campaign> {
    return this.get<Campaign>(`campaigns/${slug}`);
  }

  public async getCampaignUnits(): Promise<CampaignUnit[] | undefined> {
    const campaignId = getCampaignId(Store.getState());
    return this.get<CampaignUnit[]>(`campaign-units?campaignId=${campaignId}`);
  }

  /**
   * Lottery Endpoints:
   */
  public async createLotteryParticipant(
    gameIndex?: number
  ): Promise<Participant> {
    return this.post<Participant>('participants/with-lottery', {
      campaignGameId: this.getCampaignGameId(gameIndex)
    });
  }

  public async claimTicket(id: string): Promise<Prize> {
    const {
      lotteryPrize: { prize }
    } = await this.post<TicketCodeResponse>(
      `lottery-ticket-codes/${id}/lottery`
    );

    Store.dispatch(setUserPrize(prize));
    return prize;
  }

  public async runLottery(id: string): Promise<Participant> {
    return this.patch<Participant>(`participants/${id}/lottery`);
  }

  public async checkTicket(code: string): Promise<TicketCode> {
    return this.post<TicketCode>('lottery-ticket-codes/check', {
      campaignId: getCampaignId(Store.getState()),
      code
    });
  }

  /**
   * Product Endpoints:
   */
  public async getProducts(): Promise<Product[]> {
    return this.get<Product[]>('products');
  }

  public async getProduct(id: string): Promise<Product> {
    return this.get<Product>(`products/${id}`);
  }

  public async placeOrder(order: Order): Promise<ProductOrder> {
    return this.post<ProductOrder>('products/order', {
      casAuthToken: await this.getAuthToken(),
      ...order
    });
  }

  /**
   * Challenge Endpoints:
   */
  public async getChallenges(): Promise<Challenge[]> {
    const challenges = await this.get<Challenge[]>('challenges');
    Store.dispatch(setChallengesData(challenges));
    return challenges;
  }

  public async getChallenge(id: string): Promise<Challenge> {
    const challenge = await this.get<Challenge>(`challenges/${id}`);
    Store.dispatch(setChallengeData(challenge));
    return challenge;
  }

  public async submitChallenge(id: string, token?: string): Promise<Challenge> {
    return this.post<Challenge>(`challenges/${id}/achieve`, { token });
  }

  /**
   * Quiz Endpoints:
   */
  private setCampaignQuizzes(quizzes: CampaignQuiz[]): QuizzesState {
    const now = new Date(new Date().toISOString().slice(0, -1));

    const available = quizzes.reduce((available, { quiz, isAnswered }) => {
      let startDate = new Date(quiz.startTime ?? now);
      let endDate = new Date(quiz.endTime ?? now);

      startDate = new Date(startDate.toISOString().slice(0, -1));
      endDate = new Date(endDate.toISOString().slice(0, -1));

      return available + +(startDate < now && now < endDate && !isAnswered);
    }, 0);

    const availableQuizzes = { quizzes, available };
    Store.dispatch(setQuizzesData(availableQuizzes));

    return availableQuizzes;
  }

  public async getCampaignQuizzes(): Promise<QuizzesState> {
    if (!this.startedRequests('quizzes')) {
      const campaignId = getCampaignId(Store.getState());

      const quizzes = await this.get<CampaignQuiz[]>('quizzes', {
        campaignId
      });

      return this.setCampaignQuizzes(quizzes);
    }

    throw new Error('Already fetching quizzes.');
  }

  public async answerCampaignQuiz(
    quizId: string,
    answerId: string
  ): Promise<QuizAnswer> {
    const {
      quizzes,
      answer: {
        isCorrect,
        quiz: { correctAnswerExplanation }
      }
    } = await this.post<QuizAnswerResponse>(`quizzes/${quizId}/answer`, {
      campaignId: getCampaignId(Store.getState()),
      answerOptionId: answerId
    });

    quizzes && this.setCampaignQuizzes(quizzes);

    return {
      explanation: correctAnswerExplanation,
      correct: isCorrect,
      refresh: !quizzes
    };
  }

  /**
   * Response Methods:
   */
  public async getResponseErrorInfo(
    response: Response
  ): Promise<ResponseErrorInfo> {
    const { status: code, statusText } = response;
    const errorData = { code, error: statusText };

    try {
      const {
        message: cause,
        statusCode: code,
        error
      } = (await response.json()) as {
        statusCode?: number | number[];
        message?: string;
        error?: string;
      };

      const message = (Array.isArray(cause) && cause.join('\n')) || cause;
      Object.assign(errorData, { message, error, code });
    } catch {
      return errorData;
    }

    return errorData;
  }
}

export default new APIService();
